import EventNames from '../../../config/eventNames';
import { getContainers, getServices } from '../../../infra/startup';
import { AccessControlInputType } from '../../../infra/startup/types';
import IEventService from '../../../services/eventService/IEventService';
import bindAllMethods from '../../../utils/bindAllMethods';
import { PromiseHelper } from '../../../utils/PromiseHelper';
import { Refreshable } from '../../../utils/refreshable/IRefreshable';
import { IContainer } from '../../../core/containers';
import {
  AccessControlOptionsType,
  RefreshType,
  AccessControlInterfaceType
} from './types';
import { Scope } from '../../../services/scope';
import userContextEnumToAuthContextEnum from '../../../services/authTokenService/utils/userContextEnumToAuthContextEnum';
import { IScopeService } from '../../../services/scope/IScopeService';
import { IActionService } from '../../../services/action/types';
import IApplicationService from '../../../services/applicationService/IApplicationService';
import AuthenticationProviderEnum from '../../../config/authenticationProviderEnum';
import { UserContextEnum } from '../../types';

export class AccessControlInterface extends Refreshable {
  private defaultKey = 'default';
  private initializedUserContexts: Map<
    UserContextEnum | typeof this.defaultKey,
    boolean
  > = new Map();
  private promiseHelper = new PromiseHelper();

  private eventsService: IEventService;
  private accessControlContainer: IContainer;
  private scopeService: IScopeService;
  private actionService: IActionService;
  private applicationService: IApplicationService;
  private enabled: boolean;
  private manifestActions: string[];
  private actionsPrefix: string;

  constructor(accessControl?: AccessControlInputType) {
    super();
    const services = getServices();
    const containers = getContainers();

    this.accessControlContainer = containers.accessControlContainer;
    this.eventsService = services.eventService;
    this.scopeService = services.scopeService;
    this.actionService = services.actionService;
    this.applicationService = services.applicationService;

    const {
      enabled,
      actions: manifestActions,
      actionsPrefix
    } = accessControl || {};

    this.enabled = enabled;
    this.manifestActions = manifestActions || [];
    this.actionsPrefix = actionsPrefix;

    bindAllMethods(this);

    // new schema
    this.setRefreshMethods({
      init: this._initialize.bind(this),
      refresh: this.refresh.bind(this)
    });

    // TODO: Remove this
    // old schema
    this.accessControlContainer.addInitServices(
      this._initialize,
      undefined,
      'AccessControlInterface'
    );
    this.accessControlContainer.addRefreshServices(
      this.refresh,
      undefined,
      'AccessControlInterface'
    );
  }

  getInterface(): AccessControlInterfaceType {
    return {
      refresh: this.refresh,
      checkScopes: this.checkScopes,
      check: this.check
    };
  }

  private async refreshPromise(
    options?: AccessControlOptionsType
  ): Promise<boolean> {
    const policiesChanged = this.isProviderOneCloud()
      ? await this.actionService.refresh(
          userContextEnumToAuthContextEnum(options?.userContext)
        )
      : await this.scopeService.updateScopes(options);

    if (!policiesChanged) return false;

    void this.eventsService.publish(
      EventNames.shellAccessControlChangedEventName,
      undefined
    );

    return true;
  }

  async refresh(
    refreshOptions?: RefreshType,
    options?: AccessControlOptionsType
  ): Promise<void> {
    const promiseKey = options?.userContext ?? this.defaultKey;
    let promise = this.promiseHelper.getPendingPromise(promiseKey);
    if (this.isEnabled() && !promise) {
      promise = this.refreshPromise(options);
      this.promiseHelper.setPendingPromise(promiseKey, promise);
    }

    if (promise) {
      await promise;
    }
  }

  /**
   * During initialization, if AuthProvider is OneCloud, uses the initial
   * policies (actions) from the manifest.
   * If AuthZ, refreshes all the policies (scopes) from the API.
   */
  private async _initialize(options?: AccessControlOptionsType): Promise<void> {
    const contextKey = options?.userContext ?? this.defaultKey;
    let promise = this.promiseHelper.getPendingPromise(contextKey);
    const isInitialized = this.initializedUserContexts.get(contextKey);

    if (!isInitialized) {
      this.initializedUserContexts.set(contextKey, true);
      if (!promise) {
        promise = this.isProviderOneCloud()
          ? this.actionService.addIfNew(
              userContextEnumToAuthContextEnum(options?.userContext),
              this.actionService.normalizeActionNames(
                this.manifestActions,
                this.actionsPrefix
              )
            )
          : this.refresh({ reloadContent: false }, options);
        this.promiseHelper.setPendingPromise(contextKey, promise);
      }
    }

    if (promise) {
      await promise;
    }
  }

  /**
   * @deprecated Use `check` instead.
   */
  async checkScopes(
    scopesToCheck: Scope[],
    options: AccessControlOptionsType
  ): Promise<boolean> {
    if (!this.isEnabled()) return true;

    await this._initialize(options);
    //TODO: get the info for the user token based on the useContext
    return this.scopeService.checkScopes(scopesToCheck, options);
  }

  async check(
    policies: string[],
    options: AccessControlOptionsType
  ): Promise<boolean> {
    if (!this.isEnabled()) return true;

    await this._initialize(options);

    if (this.isProviderOneCloud()) {
      const authContext = userContextEnumToAuthContextEnum(
        options?.userContext
      );
      return this.actionService.check(
        authContext,
        this.actionService.normalizeActionNames(policies, this.actionsPrefix)
      );
    }

    return this.scopeService.checkScopes(
      this.scopeService.convertToScopes(policies),
      options
    );
  }

  isEnabled(): boolean {
    if (!this.enabled) {
      console.warn('AccessControl: disabled');
      return false;
    }
    return true;
  }

  private isProviderOneCloud(): boolean {
    return (
      this.applicationService.getAuthenticationProvider() ===
      AuthenticationProviderEnum.coptor
    );
  }
}
